<template>
  <RouterLink
    :to="to"
    :class="[
      getThemeDisplay,
      `${getThemeDisplay}-${themeColor}`,
      getButtonSize,
      getButtonStyle,
      getButtonOpacity,
      getClassDisabled,
      {
        'ds-button-icon': icon,
        'ds-button-outline': outline,
        'pointer-events-none': isDisabled,
        'ds-link-tooltip': !!tooltip,
      },
    ]"
    data-testid="ds-link"
  >
    <template v-if="!loading">
      <DsTooltip v-if="!!tooltip" :content="tooltip" :close-delay="0" portal>
        <template #activator>
          <slot />
        </template>
      </DsTooltip>
      <slot v-else />
    </template>
    <span v-if="loading" :class="`ds-${themeDisplay}-loader`">
      <DsLoader :theme-color="getLoaderThemeColor" />
      <span
        v-if="!hideLoadingText"
        :class="`ds-${themeDisplay}-loader-text`"
        :data-testid="`ds-${themeDisplay}-loader-text`"
      >
        {{ getLoaderText }}
      </span>
    </span>
  </RouterLink>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { PropType } from "vue";
import { useI18n } from "vue-i18n";
import type { RouteLocationRaw } from "vue-router";
import { RouterLink } from "vue-router";

import DsLoader from "../loader/DsLoader.vue";
import DsTooltip from "../tooltip/DsTooltip.vue";

import type {
  DsDisplayType,
  DsOpacity,
  DsThemeColor,
  DsThemeSize,
} from "../../types";

const { t } = useI18n();

defineOptions({
  name: "DsLink",
});

const props = defineProps({
  to: {
    type: [String, Object] as PropType<RouteLocationRaw>,
    required: true,
  },
  themeDisplay: {
    type: String as PropType<DsDisplayType>,
    required: false,
    default: "link",
  },
  themeColor: {
    type: String as PropType<DsThemeColor>,
    required: false,
    default: "black",
  },
  outline: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String as PropType<DsThemeSize>,
    required: false,
    default: "md",
  },
  icon: {
    type: Boolean,
    required: false,
    default: false,
  },
  pill: {
    type: Boolean,
    default: true,
  },
  opacity: {
    type: String as PropType<DsOpacity>,
    default: "100",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  hideLoadingText: {
    type: Boolean,
    default: false,
  },
  loadingLabel: {
    type: String,
    required: false,
  },
  tooltip: {
    type: String,
    required: false,
  },
});

const getThemeDisplay = computed((): string =>
  props.themeDisplay === "button" ? "ds-button" : "ds-link"
);
const getButtonSize = computed((): string =>
  props.themeDisplay === "button" ? `ds-button-${props.size}` : ""
);
const getButtonStyle = computed((): string =>
  props.themeDisplay === "button" && props.pill ? "ds-button-pill" : ""
);
const getButtonOpacity = computed((): string =>
  Number(props.opacity) < 100 ? `ds-button-opacity-${props.opacity}` : ""
);
const getClassDisabled = computed((): string => {
  return isDisabled.value ? `ds-${props.themeDisplay}-disabled` : "";
});
const getLoaderThemeColor = computed((): DsThemeColor => {
  return props.outline && ["black", "yellow"].includes(props.themeColor)
    ? "black"
    : "white";
});
const getLoaderText = computed((): string => {
  return props.loadingLabel
    ? props.loadingLabel
    : t("ds.component.button.loading");
});
const isDisabled = computed((): boolean => {
  return props.disabled || props.loading;
});
</script>

<style lang="scss" scoped>
.ds-link-tooltip {
  @apply relative;
  :deep(.ds-tooltip-activator) {
    @apply before:block before:absolute before:-inset-0;
  }
}
</style>
